import React from 'react'
import {
  Box,
  Typography,
  List,
  ListItemButton,
  Divider,
  Collapse,
  IconButton,
  Button,
  Stack,
  Paper,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { Message } from '../../../services/campaignApi'
import { formatCustomDateTime } from '../../../utils/dateUtils'
import { getStatusDisplay } from './utils'
import { GridPaginationModel } from '@mui/x-data-grid'

export interface MobileMessageListProps {
  messages: Message[]
  isLoading: boolean
  totalMessages: number
  paginationModel: GridPaginationModel
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>
  expandedMessageId: string | null
  handleToggleExpand: (e: React.MouseEvent, msgId: string) => void
  onOpenConversation: (msg: Message) => void
  renderNoRowsOverlay: () => React.ReactNode
  renderLoadingOverlay: () => React.ReactNode
}

export const MobileMessageList: React.FC<MobileMessageListProps> = ({
  messages,
  isLoading,
  totalMessages,
  paginationModel,
  setPaginationModel,
  expandedMessageId,
  handleToggleExpand,
  onOpenConversation,
  renderNoRowsOverlay,
  renderLoadingOverlay,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid rgba(224, 224, 224, 1)',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        minHeight: 632,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {renderLoadingOverlay()}
        </Box>
      ) : messages.length === 0 ? (
        <Box sx={{ py: 8, flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          {renderNoRowsOverlay()}
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1 }}>
          <List disablePadding>
          {messages.map((message) => {
            const isExpanded = expandedMessageId === message.id
            const statusDisplay = getStatusDisplay(message)
            const mainTimestamp = message.read_at || message.delivered_at || message.sent_at

            return (
              <React.Fragment key={message.id}>
                <ListItemButton
                  onClick={() => onOpenConversation(message)}
                  disabled={!message.conversation_id}
                  sx={{
                    py: 2,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    {/* Left side: Name and Phone */}
                    <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ mb: 0.25, fontSize: '0.95rem' }}>
                        {message.lead_name}
                      </Typography>
                      {message.lead_phone && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          {message.lead_phone}
                        </Typography>
                      )}
                    </Box>

                    {/* Right side: Status, Time, Chevron */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: statusDisplay.color }}>
                          {statusDisplay.icon}
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                            {statusDisplay.label}
                          </Typography>
                        </Box>
                        {mainTimestamp && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.25, whiteSpace: 'nowrap' }}>
                            {formatCustomDateTime(mainTimestamp)}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleExpand(e, message.id)}
                        sx={{ p: 0.5, mt: -0.5, mr: -1 }}
                      >
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Expanded Delivery Details Timeline */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit sx={{ width: '100%', mt: 2 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.015)',
                        border: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Delivery details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, position: 'relative' }}>
                        {/* Vertical Timeline Line */}
                        <Box sx={{ position: 'absolute', left: 9, top: 12, bottom: 12, width: 2, bgcolor: 'rgba(0,0,0,0.08)', zIndex: 0 }} />

                        {/* Sent Step */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, zIndex: 1 }}>
                          <Box sx={{ bgcolor: 'background.paper', borderRadius: '50%', color: message.is_sent ? 'info.main' : 'text.disabled', display: 'flex' }}>
                            <CheckCircleIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ mt: -0.25 }}>
                            <Typography variant="body2" fontWeight={600} color={message.is_sent ? 'text.primary' : 'text.disabled'}>
                              Sent
                            </Typography>
                            {message.sent_at && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatCustomDateTime(message.sent_at)}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Delivered Step */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, zIndex: 1 }}>
                          <Box sx={{ bgcolor: 'background.paper', borderRadius: '50%', color: message.is_delivered ? 'info.main' : 'text.disabled', display: 'flex' }}>
                            <CheckCircleIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ mt: -0.25 }}>
                            <Typography variant="body2" fontWeight={600} color={message.is_delivered ? 'text.primary' : 'text.disabled'}>
                              Delivered
                            </Typography>
                            {message.delivered_at && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatCustomDateTime(message.delivered_at)}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Read Step */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, zIndex: 1 }}>
                          <Box sx={{ bgcolor: 'background.paper', borderRadius: '50%', color: message.is_read ? 'info.main' : 'text.disabled', display: 'flex' }}>
                            <CheckCircleIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ mt: -0.25 }}>
                            <Typography variant="body2" fontWeight={600} color={message.is_read ? 'text.primary' : 'text.disabled'}>
                              Read
                            </Typography>
                            {message.read_at && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatCustomDateTime(message.read_at)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </ListItemButton>
                <Divider component="li" />
              </React.Fragment>
            )
          })}
          </List>
        </Box>
      )}

      {/* Mobile Pagination */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          borderTop: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          {totalMessages > 0
            ? `Showing ${paginationModel.page * paginationModel.pageSize + 1}-${Math.min(
              (paginationModel.page + 1) * paginationModel.pageSize,
              totalMessages
            )} of ${totalMessages}`
            : '0 messages'}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            variant="outlined"
            size="small"
            fullWidth
            disabled={paginationModel.page === 0 || isLoading}
            onClick={() =>
              setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))
            }
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            disabled={
              (paginationModel.page + 1) * paginationModel.pageSize >= totalMessages ||
              isLoading
            }
            onClick={() =>
              setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))
            }
          >
            Next
          </Button>
        </Stack>
      </Box>
    </Paper>
  )
}
